/**
 * POS Products Hooks
 *
 * React Query hooks for POS Products data fetching.
 */

import { useQuery } from '@tanstack/react-query';
import type { PosProductListParams } from '@bstock/shared';
import { posProductsService } from '../services/pos-products.service';

/**
 * Hook to fetch paginated POS products list
 */
export function usePosProducts(params: PosProductListParams & { page: number }) {
  return useQuery({
    queryKey: ['pos-products', params],
    queryFn: () => posProductsService.list(params),
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch POS product by ID
 */
export function usePosProduct(id: string | undefined) {
  return useQuery({
    queryKey: ['pos-products', id],
    queryFn: () => posProductsService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch distinct categories
 */
export function usePosProductCategories() {
  return useQuery({
    queryKey: ['pos-products', 'categories'],
    queryFn: () => posProductsService.getCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch sync status
 */
export function usePosSyncStatus() {
  return useQuery({
    queryKey: ['pos-products', 'sync-status'],
    queryFn: () => posProductsService.getSyncStatus(),
    staleTime: 30 * 1000, // 30 seconds
  });
}
